import get from 'lodash/get';
import mergeWith from 'lodash/mergeWith';
import uniqBy from 'lodash/uniqBy';

import {
  CITY_FIELD_V2,
  CONTACT_FIELD_V2,
  EMAIL_FIELD_V2,
  FIRST_NAME_FIELD_V2,
  LAST_NAME_FIELD_V2,
  LOCATION_FIELD_V2,
  PHONE_FIELD_V2,
  RESUME_V2_FIELD,
  SECTION_OVERRIDES_FIELD,
  SECTIONS_FIELD_V2,
  STATE_FIELD_V2,
  STREET_FIELD_V2,
  TEMPLATE_FIELD_V2,
  ZIP_CODE_FIELD_V2,
} from '@glass/web/modules/resumes/fieldKeys';

import sectionsPath from '@glass/shared/modules/resumes/paths/sectionsPath';

const isResumeContact = ({
  [LOCATION_FIELD_V2]: location,
  [PHONE_FIELD_V2]: phone,
  [EMAIL_FIELD_V2]: email,
}) =>
  phone ||
  email ||
  (location &&
    (location[STREET_FIELD_V2] ||
      location[CITY_FIELD_V2] ||
      location[STATE_FIELD_V2] ||
      location[ZIP_CODE_FIELD_V2]));

const isResumeHeader = ({ [FIRST_NAME_FIELD_V2]: firstName, [LAST_NAME_FIELD_V2]: lastName }) =>
  firstName || lastName;

const customMerge = (objValue, srcValue, key) => {
  switch (key) {
    case SECTION_OVERRIDES_FIELD:
      return srcValue;
    case CONTACT_FIELD_V2:
      // eslint-disable-next-line no-case-declarations
      const merge = { ...objValue };
      // eslint-disable-next-line no-case-declarations
      const {
        [FIRST_NAME_FIELD_V2]: firstName,
        [LAST_NAME_FIELD_V2]: lastName,
        [LOCATION_FIELD_V2]: location,
        [PHONE_FIELD_V2]: phone,
        [EMAIL_FIELD_V2]: email,
      } = srcValue || {};
      if (isResumeHeader(srcValue || {})) {
        merge[FIRST_NAME_FIELD_V2] = firstName;
        merge[LAST_NAME_FIELD_V2] = lastName;
      }
      if (isResumeContact(srcValue || {})) {
        merge[LOCATION_FIELD_V2] = location;
        merge[PHONE_FIELD_V2] = phone;
        merge[EMAIL_FIELD_V2] = email;
      }
      return merge;
    case TEMPLATE_FIELD_V2:
      return srcValue || objValue;
    default:
      break;
  }
  if (Array.isArray(objValue) && Array.isArray(srcValue)) {
    switch (key) {
      case SECTIONS_FIELD_V2:
        return uniqBy([...srcValue, ...objValue], 'type');
      default:
        return srcValue.length ? srcValue : objValue;
    }
  }
  return undefined;
};

// NOTE: mergeWith mutates the first object which produces some serious side effects
const mergePlaceholderResume = (resume, placeholder) => {
  if (!resume) {
    return {};
  }
  if (!placeholder) {
    return { resume: { ...resume } };
  }
  const resumeContact = resume?.[RESUME_V2_FIELD]?.[CONTACT_FIELD_V2] || {};
  const placeholderSections = get(placeholder, sectionsPath, []);
  const resumeSections = get(resume, sectionsPath, []);
  const sectionClasses = placeholderSections.reduce(
    (classes, { type }) => {
      return {
        [type]: {
          disabled: resumeSections.every(({ type: resumeType }) => type !== resumeType),
        },
        ...classes,
      };
    },
    {
      header: {
        disabled: !isResumeHeader(resumeContact),
      },
      [CONTACT_FIELD_V2]: {
        disabled: !isResumeContact(resumeContact),
      },
    },
  );

  const mergedResume = mergeWith({}, placeholder, resume, customMerge);
  return { resume: mergedResume, sectionClasses };
};

export default mergePlaceholderResume;
